import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm } from '@upme/api-client';
import {
  EventCategoryCreateInput as ECCreate,
  EventCategoryUpdateInput as ECUpdate,
  type EventCategoryCreateInput,
  type EventCategoryDto,
  type EventCategoryUpdateInput,
} from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Switch } from '@upme/ui/components/switch';
import { type Resolver, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { collectRootMessages } from '../../../app/form-errors';
import { zodResolver } from '../../../app/zod-resolver';
import { useCreateEventCategory, useUpdateEventCategory } from '../mutations';

type CreateProps = { mode: 'create'; readOnly?: boolean };
type EditProps = { mode: 'edit'; category: EventCategoryDto; readOnly?: boolean };
type Props = CreateProps | EditProps;

type FormValues = EventCategoryCreateInput & Partial<EventCategoryCreateInput>;

export function EventCategoryForm(props: Props) {
  const router = useRouter({ warn: false });
  const isCreate = props.mode === 'create';
  const readOnly = props.readOnly === true;
  const schema = isCreate ? ECCreate : ECUpdate.loose();

  const defaults: Partial<FormValues> = isCreate
    ? ({ sortOrder: 0, isActive: true } as Partial<FormValues>)
    : {
        slug: props.category.slug,
        name: props.category.name,
        // DTO допускает icon: null, Zod-схема — только string|undefined.
        // Без нормализации Zod-resolver отбрасывает submit ещё до mutate.
        icon: props.category.icon ?? undefined,
        sortOrder: props.category.sortOrder,
        isActive: props.category.isActive,
      };

  const form = useForm<FormValues>({
    resolver: zodResolver(schema as unknown as z.ZodType<FormValues>) as Resolver<FormValues>,
    defaultValues: defaults as FormValues,
    mode: 'onSubmit',
  });

  const create = useCreateEventCategory();
  const update = useUpdateEventCategory(isCreate ? '' : props.category.id);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      let result: EventCategoryDto;
      if (isCreate) {
        result = await create.mutateAsync(values as EventCategoryCreateInput);
      } else {
        const body: EventCategoryUpdateInput = {
          name: values.name,
          icon: values.icon || undefined,
          sortOrder: values.sortOrder,
          isActive: values.isActive,
        };
        result = await update.mutateAsync(body);
      }
      toast.success(isCreate ? 'Категория создана' : 'Изменения сохранены');
      router?.navigate({ to: '/event-categories/$categoryId', params: { categoryId: result.id } });
    } catch (err) {
      await mapApiErrorToForm(err, form, { rootField: 'slug' });
    }
  });

  const slugError = form.formState.errors.slug;
  const nameError = form.formState.errors.name;
  const iconError = form.formState.errors.icon;
  const sortOrderError = form.formState.errors.sortOrder;
  const rootMessages = collectRootMessages(form.formState.errors as Record<string, unknown>);

  return (
    <form
      onSubmit={readOnly ? (e) => e.preventDefault() : onSubmit}
      className="space-y-4 max-w-xl"
      noValidate
    >
      <div>
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          {...form.register('slug')}
          disabled={readOnly || !isCreate}
          placeholder="creativity"
        />
        {slugError && <p className="text-sm text-destructive mt-1">{String(slugError.message)}</p>}
        <p className="text-xs text-muted-foreground mt-1">lowercase, цифры, дефисы. Менять нельзя.</p>
      </div>

      <div>
        <Label htmlFor="name">Название</Label>
        <Input id="name" {...form.register('name')} disabled={readOnly} placeholder="Творчество" />
        {nameError && <p className="text-sm text-destructive mt-1">{String(nameError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="icon">Иконка (эмодзи)</Label>
        <Input id="icon" {...form.register('icon')} disabled={readOnly} placeholder="🎨" maxLength={8} />
        {iconError && <p className="text-sm text-destructive mt-1">{String(iconError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="sortOrder">Порядок сортировки</Label>
        <Input
          id="sortOrder"
          type="number"
          min={0}
          max={10000}
          {...form.register('sortOrder', { valueAsNumber: true })}
          disabled={readOnly}
        />
        {sortOrderError && <p className="text-sm text-destructive mt-1">{String(sortOrderError.message)}</p>}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="isActive"
          checked={form.watch('isActive') ?? true}
          disabled={readOnly}
          onCheckedChange={(v) => form.setValue('isActive', v)}
        />
        <Label htmlFor="isActive">Активна</Label>
      </div>

      {rootMessages.map((m) => (
        <p key={m} className="text-sm text-destructive">
          {m}
        </p>
      ))}

      {!readOnly && (
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {isCreate ? 'Создать' : 'Сохранить'}
        </Button>
      )}
    </form>
  );
}
